
import React, { useState, useEffect } from 'react';
import { AppConfig, User } from '../types';
import SettingsHeader from './Settings/SettingsHeader';
import SettingsTabs from './Settings/SettingsTabs';
import ProfileTab from './Settings/ProfileTab';
import AppPrefsTab from './Settings/AppPrefsTab';
import SecurityTab from './Settings/SecurityTab';
import SettingsScanner from './Settings/SettingsScanner';
import SettingsFooter from './Settings/SettingsFooter';

interface SettingsProps {
  config: AppConfig;
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (config: Partial<AppConfig>) => void;
  onUpdateUser: (userData: Partial<User>) => void;
  onLogout?: () => void;
  onDeleteAccount?: () => void;
  onRestartTour?: () => void;
  initialTab?: 'profile' | 'app' | 'security';
}

const Settings: React.FC<SettingsProps> = ({ config, user, isOpen, onClose, onUpdate, onUpdateUser, onLogout, onDeleteAccount, onRestartTour, initialTab }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'app' | 'security'>(initialTab || 'app');
  const [isScanning, setIsScanning] = useState(false);
  
  const isRtl = config.language === 'ar';

  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 ${isRtl ? 'rtl' : 'ltr'}`}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />
      
      <SettingsScanner 
        isScanning={isScanning} 
        onScanSuccess={(text) => {
           onUpdate({ apiKey: text });
           setIsScanning(false);
        }} 
        onClose={() => setIsScanning(false)} 
      />

      <div className="relative w-full max-w-2xl bg-white dark:bg-[#0f0f13] rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-200 dark:border-white/10 flex flex-col max-h-[85vh]">
        
        <SettingsHeader language={config.language} onClose={onClose} />

        <SettingsTabs language={config.language} activeTab={activeTab} setActiveTab={setActiveTab} />

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
          {activeTab === 'profile' && (
            <ProfileTab user={user} language={config.language} onUpdateUser={onUpdateUser} />
          )}

          {activeTab === 'app' && (
            <AppPrefsTab config={config} onUpdate={onUpdate} onRestartTour={onRestartTour} />
          )}

          {activeTab === 'security' && (
            <SecurityTab 
              config={config} 
              language={config.language} 
              onUpdate={onUpdate} 
              onDeleteAccount={onDeleteAccount} 
              onStartScanning={() => setIsScanning(true)} 
            />
          )}
        </div>

        <SettingsFooter language={config.language} onClose={onClose} />

      </div>
    </div>
  );
};

export default Settings;
